'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { Heart } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { ProductBase } from '@/types/product';
import { generateColor } from '@/utils/productMapper';
import { useAddToCart } from '@/hooks/useAddToCart';
import { CartItem } from '@/types/cart';
import { useCartTracking } from '@/hooks/useCartTracking';

// Función para convertir hex a rgba
function hexToRgba(hex: string, alpha: number): string {
  if (!hex || typeof hex !== 'string') {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  hex = hex.replace('#', '');

  if (hex.length !== 6) {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  const bigint = parseInt(hex, 16);

  if (isNaN(bigint)) {
    return 'rgba(255, 222, 59, 0.26)'; // Fallback a amarillo
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  const validAlpha = Math.max(0, Math.min(1, alpha));

  return `rgba(${r}, ${g}, ${b}, ${validAlpha})`;
}

// Función para formatear precios con coma
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',');
}

interface RelatedProductsSectionMobileProps {
  onFavoriteToggle?: (id: string) => void;
  relatedProducts: ProductBase[];
}

const RelatedProductsSectionMobile: React.FC<RelatedProductsSectionMobileProps> = ({
  onFavoriteToggle,
  relatedProducts,
}) => {
  const router = useRouter();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { addToCart } = useAddToCart();
  const { trackAddToCartEvent } = useCartTracking();

  const handleFavoriteToggle = (id: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  const handleAddToCart = (product: ProductBase) => {
    const cartItem: CartItem = {
      id: product.id.toString(),
      name: product.title,
      image: product.image,
      price: product.priceFinalInc,
      tax: product.priceFinalInc - product.priceFinalExc,
      quantity: 1,
      weight: product.weight || '',
      brand: product.brand || '',
      sku: product.slug,
      barcode: product.barcode || '',
    };
    addToCart(cartItem);
    trackAddToCartEvent(cartItem);
  };

  if (relatedProducts.length === 0) {
    return null;
  }

  return (
    <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:92:4" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="92" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-2%22%7D" className="py-2">
      <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:93:6" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="93" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20mb-2%20text-left%22%7D" className="px-4 mb-2 text-left">
        <p data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:94:8" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="94" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-2xl%20leading-7%20text-global-1%20mb-1%22%2C%22textContent%22%3A%22QUIZ%C3%81%20TAMBI%C3%89N%20TE%20GUSTE...%22%7D" className="font-tt-norms-extra-black text-2xl leading-7 text-global-1 mb-1">
          QUIZÁ TAMBIÉN TE GUSTE...
        </p>
        <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:97:8" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="97" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-3%20inline-block%22%7D" className="bg-global-9 rounded px-3 inline-block">
          <span data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:98:10" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="98" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-sm%20leading-4%20text-global-1%22%2C%22textContent%22%3A%22Seguro%20que%20te%20interesa%22%7D" className="font-tt-norms-medium text-sm leading-4 text-global-1">
            Seguro que te interesa
          </span>
        </div>
      </div>

      {/* Grid de productos 2x2 */}
      <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:105:6" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="105" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-2%20gap-2%20px-4%20mb-3%20items-stretch%22%7D" className="grid grid-cols-2 gap-2 px-4 mb-3 items-stretch">
        {relatedProducts.map((product, index) => {
          // Calcular descuento (igual que en desktop)
          const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
          const originalPriceNumber =
            product?.priceOriginalInc !== undefined
              ? parseFloat(String(product?.priceOriginalInc))
              : undefined;
          const hasDiscount =
            originalPriceNumber !== undefined &&
            !isNaN(originalPriceNumber) &&
            originalPriceNumber > currentPriceNumber;
          const discountPercent = hasDiscount
            ? Math.round(((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100)
            : 0;

          const backgroundColor = generateColor(index) || '#FFDE3B';
          const backgroundOpacity = 0.45; // Mismo que en GlutenFreeSectionMobile

          const backgroundStyle = {
            backgroundColor: hexToRgba(backgroundColor, backgroundOpacity),
          };

          return (
            <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:129:12" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="129" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B12px%5D%20p-3%20relative%20shadow-sm%20flex%20flex-col%20w-full%22%7D"
              key={product.id}
              className="rounded-[12px] p-3 relative shadow-sm flex flex-col w-full"
              style={{
                ...backgroundStyle,
                minHeight: '245px',
              }}
            >
              {/* Badge de descuento */}
              {hasDiscount && (
                <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:139:16" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="139" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-2%20right-2%20z-10%20bg-%5B%23fcc00a%5D%20px-2%20py-1%20rounded-full%20shadow-md%22%7D" className="absolute top-2 right-2 z-10 bg-[#fcc00a] px-2 py-1 rounded-full shadow-md">
                  <span data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:140:18" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="140" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-%5B10px%5D%20font-bold%22%2C%22textContent%22%3A%22-%20%25%22%7D"
                    className="text-[#3d1c1a] text-[10px] font-bold"
                    style={{
                      fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                      fontWeight: '700',
                    }}
                  >
                    -{discountPercent}%
                  </span>
                </div>
              )}

              {/* Imagen del producto */}
              <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:153:14" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="153" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-28%20mb-3%20flex-shrink-0%20mt-2%20overflow-hidden%22%7D" className="flex justify-center items-center h-28 mb-3 flex-shrink-0 mt-2 overflow-hidden">
                <Image data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:154:16" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="154" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22object-contain%20cursor-pointer%22%7D"
                  src={product.image}
                  alt={product.title}
                  width={100}
                  height={100}
                  className="object-contain cursor-pointer"
                  onClick={() => router.push(`/${product.slug}`)}
                />
              </div>

              {/* Información del producto */}
              <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:165:14" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="165" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-left%20flex-1%20flex%20flex-col%20justify-between%22%7D" className="text-left flex-1 flex flex-col justify-between">
                <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:166:16" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="166" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <p data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:167:18" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="167" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-4%20text-%5B%233d1c1a%5D%20mb-1%20cursor-pointer%20line-clamp-2%20min-h-8%22%7D"
                    className="text-sm leading-4 text-[#3d1c1a] mb-1 cursor-pointer line-clamp-2 min-h-8"
                    style={{
                      fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                      fontWeight: '700',
                    }}
                    onClick={() => router.push(`/${product.slug}`)}
                  >
                    {product.title}
                  </p>
                  {product.brand && (
                    <p data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:178:20" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="178" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-%5B%233d1c1a%5D%20mb-1%22%7D"
                      className="text-xs text-[#3d1c1a] mb-1"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                        fontWeight: '400',
                      }}
                    >
                      {product.brand}
                    </p>
                  )}
                  {product.productCombinations && product.productCombinations.length > 0 && (
                    <span data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:189:20" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="189" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-%5B%233d1c1a%5D%20mb-1%20block%22%7D"
                      className="text-xs text-[#3d1c1a] mb-1 block"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                        fontWeight: '400',
                      }}
                    >
                      {product.productCombinations
                        .find((combination) => combination.isDefault)
                        ?.productOptions?.map((option) => option.name)
                        .join(' / ')}
                    </span>
                  )}
                </div>

                {/* Precio */}
                <div data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:205:16" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="205" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20mt-auto%20pr-10%22%7D" className="flex flex-col mt-auto pr-10">
                    <span data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:206:20" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="206" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-%5B%233d1c1a%5D%20whitespace-nowrap%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                      className="text-sm text-[#3d1c1a] whitespace-nowrap"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                        fontWeight: '700',
                      }}
                    >
                      {formatPrice(currentPriceNumber)} €/ud.
                    </span>
                    {hasDiscount && originalPriceNumber && (
                      <span data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:216:22" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="216" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20text-%5B%233d1c1a%5D%2F60%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                        className="text-[10px] text-[#3d1c1a]/60 line-through"
                        style={{
                          fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                          fontWeight: 400,
                        }}
                      >
                        {formatPrice(originalPriceNumber)} €/ud.
                      </span>
                    )}
                </div>
              </div>

              <button data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:229:14" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="229" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20bottom-3%20right-3%20bg-%5B%23FFC700%5D%20rounded-%5B10px%5D%20w-8%20h-8%20flex%20items-center%20justify-center%20hover%3Abg-%5B%23FFC700%5D%2F90%20transition-all%20duration-300%20hover%3Ascale-110%20flex-shrink-0%22%7D"
                onClick={() => handleAddToCart(product)}
                className="absolute bottom-3 right-3 bg-[#FFC700] rounded-[10px] w-8 h-8 flex items-center justify-center hover:bg-[#FFC700]/90 transition-all duration-300 hover:scale-110 flex-shrink-0"
              >
                <span data-component-id="src/components/sections/mobile/RelatedProductsSectionMobile.tsx:233:16" data-component-path="src/components/sections/mobile/RelatedProductsSectionMobile.tsx" data-component-line="233" data-component-file="RelatedProductsSectionMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-lg%20font-bold%20leading-none%22%2C%22textContent%22%3A%22%2B%22%7D" className="text-[#3d1c1a] text-lg font-bold leading-none">+</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RelatedProductsSectionMobile;
